import type { RoastUploadMessage, UploadConfig } from '$lib/roasting/types';
import { openDB, type IDBPDatabase } from 'idb';

// Database name and version
const DB_NAME = 'roastUploads';
const DB_VERSION = 1;

// Store names
export const STORES = {
  PENDING_UPLOADS: 'pendingUploads',
  FAILED_UPLOADS: 'failedUploads'
};

// Default configuration for upload retries
export const DEFAULT_CONFIG: UploadConfig = {
  maxRetries: 5,
  baseDelay: 5000, // 5 seconds
  maxDelay: 300000, // 5 minutes
  jitterFactor: 0.5 // 50% random jitter
};

// Calculate delay with exponential backoff and jitter
export function calculateBackoffDelay(retryCount: number, config: UploadConfig): number {
  // Calculate exponential backoff: baseDelay * 2^retryCount
  const exponentialDelay = config.baseDelay * Math.pow(2, retryCount);

  // Cap the delay at maxDelay
  const cappedDelay = Math.min(exponentialDelay, config.maxDelay);

  // Add random jitter
  const jitter = Math.random() * config.jitterFactor * cappedDelay;

  return cappedDelay + jitter;
}

// Initialize the database
export const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db) {
    db.createObjectStore(STORES.PENDING_UPLOADS, { keyPath: 'id' });
    db.createObjectStore(STORES.FAILED_UPLOADS, { keyPath: 'id' });
  }
});

// Store a pending upload in the database
export async function storePendingUpload(db: IDBPDatabase, data: RoastUploadMessage) {
  await db.put(STORES.PENDING_UPLOADS, {
    id: data.newRoastData.id,
    data,
    timestamp: Date.now(),
    retryCount: 0,
    nextRetryTime: Date.now()
  });
}

// Move an upload from pending to failed
export async function moveToFailed(db: IDBPDatabase, id: string, error: Error) {
  const pending = await db.get(STORES.PENDING_UPLOADS, id);
  if (pending) {
    await db.put(STORES.FAILED_UPLOADS, {
      ...pending,
      error: error.message,
      lastAttempt: Date.now()
    });
    await db.delete(STORES.PENDING_UPLOADS, id);
  }
}

// Get all pending uploads
export async function getAllPendingUploads(db: IDBPDatabase) {
  return db.getAll(STORES.PENDING_UPLOADS);
}

// Get all failed uploads
export async function getAllFailedUploads(db: IDBPDatabase) {
  return db.getAll(STORES.FAILED_UPLOADS);
}

// Get a specific pending upload
export async function getPendingUpload(db: IDBPDatabase, id: string) {
  return db.get(STORES.PENDING_UPLOADS, id);
}

// Get a specific failed upload
export async function getFailedUpload(db: IDBPDatabase, id: string) {
  return db.get(STORES.FAILED_UPLOADS, id);
}

// Delete a pending upload
export async function deletePendingUpload(db: IDBPDatabase, id: string) {
  await db.delete(STORES.PENDING_UPLOADS, id);
}

// Delete a failed upload
export async function deleteFailedUpload(db: IDBPDatabase, id: string) {
  await db.delete(STORES.FAILED_UPLOADS, id);
}

// Update a pending upload
export async function updatePendingUpload(db: IDBPDatabase, id: string, updates: Partial<any>) {
  const pending = await db.get(STORES.PENDING_UPLOADS, id);
  if (pending) {
    await db.put(STORES.PENDING_UPLOADS, {
      ...pending,
      ...updates
    });
    return true;
  }
  return false;
}
